
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SubscriptionPlan, subscriptionPlans } from '@/types/subscriptionPlans';
import PlanEditForm from './PlanEditForm';
import { getBusinessSignupPlans, saveBusinessSignupPlans } from '@/utils/businessPlanUtils';

interface BusinessSignupPlansTabProps {
  onSaved?: () => Promise<void>;
}

const BusinessSignupPlansTab: React.FC<BusinessSignupPlansTabProps> = ({ onSaved }) => {
  const { toast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Load business signup plans using our utility function
  useEffect(() => {
    setPlans(getBusinessSignupPlans());
  }, []);

  const handleUpdatePlan = (index: number, field: keyof SubscriptionPlan, value: any) => {
    const updatedPlans = [...plans];
    updatedPlans[index] = {
      ...updatedPlans[index],
      [field]: value
    };
    setPlans(updatedPlans);
  };

  const handleSavePlans = async () => {
    setIsSaving(true);
    try {
      // Use the utility function to save plans and dispatch the event
      saveBusinessSignupPlans(plans);
      
      // In a real app, this would also make an API call to save the plans
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast({
        title: "Business Signup Plans Saved",
        description: "Business signup plans have been updated successfully",
      });

      // Call the onSaved callback if provided
      if (onSaved) {
        await onSaved();
      }
    } catch (error) {
      console.error('Error saving plans:', error);
      toast({
        title: "Error Saving Plans",
        description: "There was a problem saving the business signup plans",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addNewPlan = () => {
    const newPlan: SubscriptionPlan = {
      id: `plan-${Date.now()}`,
      name: "New Plan",
      monthlyPrice: 49,
      quarterlyPrice: 125,
      quarterlyDiscount: 15,
      description: "New business subscription plan",
      features: [
        { name: 'Custom domain', included: true },
        { name: 'White-labeled experience', included: true },
        { name: 'Email notifications', included: true },
      ],
      maxReferrals: 100,
      maxManagers: 3,
      maxReferralLinks: 10,
      recommended: false
    };
    
    setPlans([...plans, newPlan]);
  };

  const deletePlan = (index: number) => {
    const updatedPlans = plans.filter((_, i) => i !== index);
    setPlans(updatedPlans);
  };

  return (
    <Card className="border-none shadow-lg bg-gradient-to-br from-white to-blue-50">
      <CardHeader className="flex flex-row justify-between items-center pb-2">
        <div>
          <CardTitle className="text-xl text-primary-blue">Business Signup Plans</CardTitle>
          <CardDescription>
            Manage plans that appear on the business signup page. Changes here will affect what plans users see when signing up.
          </CardDescription>
        </div>
        <Button onClick={addNewPlan} className="flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md">
          <Plus className="mr-2 h-4 w-4" />
          Add New Plan
        </Button>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {plans.map((plan, index) => (
          <PlanEditForm
            key={plan.id}
            plan={plan}
            index={index}
            onUpdatePlan={handleUpdatePlan}
            onDeletePlan={deletePlan}
          />
        ))}
        
        <div className="flex justify-end">
          <Button 
            onClick={handleSavePlans} 
            disabled={isSaving}
            className="flex items-center bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-md"
          >
            {isSaving ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-b-transparent"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Business Signup Plans
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BusinessSignupPlansTab;
